/**
 * Display formatting for scene labels.
 *
 * Values are canonical SI internally (invariant 5); units appear only here, at
 * the boundary where a number becomes something a person reads.
 */

import { displayUnits, formatGrouped, fromCanonical, type DisplayUnit } from "../workspace/units";

const MICRO = "\u00b5";

/** Three significant figures, without trailing zeros: 20, 1.25, 350. */
function significant(value: number): string {
  return String(Number(value.toPrecision(3)));
}

/**
 * A length in metre: in the design's length unit where it sets one (D49), to
 * six figures so an edited length reads back as typed; otherwise in the unit a
 * microfluidics designer would say it in.
 */
export function formatLength(metres: number, unit?: DisplayUnit | null): string {
  if (unit) return `${formatGrouped(fromCanonical(metres, unit))} ${unit.label}`;
  // Round before choosing the unit, so 999.6 um reads "1 mm" not "1000 um".
  const magnitude = Math.abs(Number(metres.toPrecision(3)));
  if (magnitude >= 1) {
    return `${significant(metres)} m`;
  }
  if (magnitude >= 1e-3) {
    return `${significant(metres * 1e3)} mm`;
  }
  return `${significant(metres * 1e6)} ${MICRO}m`;
}

/** The unit an unset length unit draws `metres` in, so a field opened on the label reads as it does. */
export function lengthLabelUnit(metres: number | null): DisplayUnit {
  const magnitude = metres === null ? 1e-3 : Math.abs(Number(metres.toPrecision(3)));
  const label = magnitude >= 1 ? "m" : magnitude >= 1e-3 ? "mm" : `${MICRO}m`;
  return displayUnits("meter").find((unit) => unit.label === label)!;
}
